import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { BOOKING, IMAGES } from "@/lib/site";
import { SiteButton } from "./SiteButton";

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  animate: { opacity: 1, y: 0 },
};

const ticker = "Traditional sauna · Ice bath · Recovery deck · Red-light therapy coming soon · West Perth · ";

// ripples travelling from the deck's edge toward the viewer
const RINGS = [0, 2.2, 4.4, 6.6, 8.8];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const sceneY = useTransform(scrollYProgress, [0, 1], ["0%", "10%"]);
  const sceneScale = useTransform(scrollYProgress, [0, 1], [1, 1.16]);
  const sceneDim = useTransform(scrollYProgress, [0, 0.9], [0, 0.92]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] overflow-hidden bg-black"
    >
      {/* ── the scene: sauna floating on black water, copy anchored to the scene ── */}
      <motion.div style={{ y: sceneY, scale: sceneScale }} className="absolute inset-0 z-[2]">
        <div
          className="absolute left-1/2 -translate-x-1/2 aspect-[1672/941]"
          style={{ height: "112svh", top: "-6svh" }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative h-full w-full"
          >
          <img src={IMAGES.lake} alt="" className="h-full w-full select-none" aria-hidden="true" />

          {/* pulsating light strips */}
          <div className="sauna-glow" style={{ left: "39.5%", top: "23%", width: "20%", height: "7%", animationDelay: "0s" }} />
          <div className="sauna-glow" style={{ left: "40.5%", top: "36.5%", width: "18%", height: "8%", animationDelay: "-3s" }} />
          <div className="sauna-glow" style={{ left: "41%", top: "46%", width: "16%", height: "7%", animationDelay: "-5s" }} />
          <div className="sauna-glow sauna-glow--soft" style={{ left: "37%", top: "23%", width: "25%", height: "34%", animationDelay: "-1.5s" }} />

          {/* warm shimmer on the water reflection, mirroring the pulsing glow */}
          <div className="reflection-shimmer" style={{ left: "36%", top: "62%", width: "28%", height: "26%", animationDelay: "-3s" }} />
          <div className="reflection-shimmer reflection-shimmer--wide" style={{ left: "30%", top: "60%", width: "40%", height: "34%", animationDelay: "-1.5s" }} />

          {/* glass panels glisten either side of the open door (door pane left, fixed panel right — opening stays clear) */}
          <div
            className="absolute overflow-hidden pointer-events-none mix-blend-screen"
            style={{ left: "42%", top: "23%", width: "2.7%", height: "33%" }}
            aria-hidden="true"
          >
            <div className="absolute inset-y-0 w-1/2 -skew-x-[18deg] bg-gradient-to-r from-transparent via-white/25 to-transparent animate-sheen" />
          </div>
          <div
            className="absolute overflow-hidden pointer-events-none mix-blend-screen"
            style={{ left: "53%", top: "25%", width: "5.2%", height: "31%" }}
            aria-hidden="true"
          >
            <div className="absolute inset-y-0 w-1/3 -skew-x-[18deg] bg-gradient-to-r from-transparent via-white/25 to-transparent animate-sheen" style={{ animationDelay: "-4.5s" }} />
          </div>

          {/* steam rising off the ice bath */}
          <div className="hero-bath-steam" style={{ left: "60%", top: "33%", width: "4.5%", height: "8%", animationDelay: "0s" }} aria-hidden="true" />
          <div className="hero-bath-steam" style={{ left: "63.5%", top: "31.5%", width: "5%", height: "9%", animationDelay: "-2.6s" }} aria-hidden="true" />
          <div className="hero-bath-steam" style={{ left: "66.5%", top: "33.5%", width: "4%", height: "7.5%", animationDelay: "-5.2s" }} aria-hidden="true" />

          {/* ripples rolling from the deck toward the viewer */}
          <div className="absolute left-0 right-0 top-[58%] bottom-0 overflow-hidden pointer-events-none" aria-hidden="true">
            {RINGS.map((d, i) => (
              <span key={i} className="water-ring" style={{ animationDelay: `${d}s` }} />
            ))}
          </div>

          {/* eyebrow: on the black ground just above the sauna */}
          <div className="absolute inset-x-0 top-[13%] z-20">
            <div className="mx-auto px-5 text-center" style={{ width: "100vw", maxWidth: "100%" }}>
              <motion.p
                {...fadeUp}
                transition={{ duration: 0.9, delay: 0.5 }}
                data-testid="hero-eyebrow"
                className="mt-3 text-[10px] sm:text-xs tracking-[0.42em] uppercase font-medium text-ember [text-shadow:0_2px_18px_rgba(0,0,0,0.95)]"
              >
                Contrast therapy · West Perth
              </motion.p>
            </div>
          </div>

          {/* headline: sitting below the deck, on the water */}
          <div className="absolute inset-x-0 top-[63.5%] z-20">
            <div className="mx-auto px-5 text-center" style={{ width: "100vw", maxWidth: "100%" }}>
              <motion.h1
                {...fadeUp}
                transition={{ duration: 0.9, delay: 0.6 }}
                data-testid="hero-headline"
                className="mx-auto max-w-4xl font-display uppercase text-3xl sm:text-5xl lg:text-6xl font-normal tracking-wide leading-[1.06] text-cream [text-shadow:0_4px_40px_rgba(0,0,0,0.95)]"
              >
                Your private hour to <em className="italic text-ember">heat&nbsp;up</em>,{" "}
                <em className="italic text-ice">cool&nbsp;down</em> and reset.
              </motion.h1>
              <motion.div {...fadeUp} transition={{ duration: 0.9, delay: 0.8 }} className="mt-5 sm:mt-7">
                <SiteButton testid="hero-book-button" href={BOOKING.book}>
                  Book now
                </SiteButton>
              </motion.div>
            </div>
          </div>
          </motion.div>
        </div>
        {/* scroll story: scene dims as you scroll into the Experience section */}
        <motion.div style={{ opacity: sceneDim }} className="absolute inset-0 bg-black pointer-events-none" />
      </motion.div>

      {/* darkness pooling so type stays readable */}
      <div
        className="absolute inset-0 z-[5] pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 70% 36% at 50% 100%, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.18) 55%, transparent 82%), linear-gradient(to bottom, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 12%, transparent 26%)",
        }}
        aria-hidden="true"
      />

      {/* ticker */}
      <div className="absolute bottom-0 inset-x-0 z-20 border-t border-line/50 bg-ink/70 backdrop-blur-md overflow-hidden">
        <div className="flex w-max animate-marquee py-3">
          {[0, 1].map((i) => (
            <span
              key={i}
              aria-hidden={i === 1}
              className="whitespace-nowrap text-xs tracking-[0.3em] uppercase text-stone2 pr-2"
            >
              {ticker.repeat(4)}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
